// @workflow platform.viewer
/**
 * The viewer adapter (anti-corruption layer): ONE interface that clean code calls for the 3D
 * viewer, instead of forgeService, forgeviewer, `store.state.viewer` or MainView.
 *
 * Today every operation forwards to the legacy implementation unchanged. Over time each
 * capability moves its logic out of forgeService into clean modules behind the same operation,
 * so callers never change. The store, the legacy service instance and the viewer are read fresh
 * on every call (the store replaces `forgeService` with a new instance, store/index.js:1179).
 *
 * This file and the files it imports have no Vue or path-alias imports, so `npm run test:unit`
 * (node --test) can load them with fakes; the app's wiring is in index.js / legacy.js.
 */
import { readinessOf, isReadyState } from './readiness.js';
import { createSceneOps } from './scene.js';
import { createCameraOps } from './camera.js';
import { createCropOps } from './crop.js';
import { createViewLinkOps } from './views.js';
import { createDocumentOps } from './documents.js';
import { createLinkedViewOps } from './linkedViews.js';
import { createMiniMapOps } from './miniMap.js';

/** The pane type of the 3D viewer (utils/viewerNavigationUtils.js VIEWER_TYPES.MODEL_3D). */
export const PANE_TYPE_3D = '3d';

/** The pane type of the 2D plan (store/modules/viewers.js view<n>Type). */
export const PANE_TYPE_2D = '2d';

/** The pane type of the 360 view (utils/viewerNavigationUtils.js VIEWER_TYPES.PANORAMA_360). */
export const PANE_TYPE_360 = '360';

/** What the user is told when an operation needs the 3D viewer (forgeService.isolateTag's words). */
export const VIEWER_MESSAGES = Object.freeze({ OPEN_3D_FIRST: 'Please open 3D viewer first.' });

const REQUIRED_DEPS = Object.freeze([
  'store',
  'panes',
  'spatialFocus',
  'resolveSpatialFocus',
  'buildRenderPlan',
  'fitRenderPlan',
  'listLoadedModels',
  'themeElements',
  'toThemingColor',
  'boxOf',
  'takeScreenshot',
  'notify',
  'events',
  'unlockNavigation',
  'documents',
  'plan',
]);

function assertDeps(deps) {
  const missing = REQUIRED_DEPS.filter((name) => !deps || deps[name] === undefined || deps[name] === null);
  if (missing.length) throw new TypeError(`createViewer: missing ${missing.join(', ')}`);
}

/** Panes and 360, through what the viewer page registered (panes.js); none of it reaches a component. */
function createPaneOps(panes) {
  return {
    /** The viewer page registered its panes (MainView is created). */
    panesAvailable: () => !!panes.get(),
    /** MainView.openViewPageWithType('3d'): a free pane, or false after telling the user every pane is in use. */
    open3dPane() {
      const page = panes.get();
      return !!page && page.openPane(PANE_TYPE_3D) !== undefined;
    },
    /** MainView.openViewPageWithType('2d'): the 360 mini map's "open in a 2D pane" (AVA-660). */
    open2dPane() {
      const page = panes.get();
      return !!page && page.openPane(PANE_TYPE_2D) !== undefined;
    },
    /** MainView.showViewPageWithType('3d'). */
    show3dPane() {
      const page = panes.get();
      if (page) page.showPane(PANE_TYPE_3D);
    },
    /**
     * The view in `slot` switched to 3D through the slot's own view switcher (PageView changeView);
     * false while no viewer page is registered or it has no such slot.
     */
    switchPaneTo3d(slot) {
      const page = panes.get();
      return !!page && page.changePane(slot, PANE_TYPE_3D) === true;
    },
    /**
     * MainView.openTagIn360 (its ?zoom360 pipeline); null while no viewer page is registered.
     * `isWanted` withdraws a stopped request before it opens anything.
     */
    openTagIn360(tagId, { isWanted = () => true } = {}) {
      const page = panes.get();
      if (!page) return Promise.resolve(null);
      return page.openTagIn360(tagId, { isWanted });
    },
  };
}

/**
 * @param {{
 *   store: import('vuex').Store,   reads state.viewer, state.forgeService, state.levels and getters; commits mutations
 *   panes: { get: () => object|null },   the viewer page's registration (panes.js)
 *   spatialFocus: { show: Function, clear: Function },   useSpatialFocus()
 *   resolveSpatialFocus: (body: object, options: { signal?: AbortSignal }) => Promise<{ data: object }>,
 *   buildRenderPlan: (input: object) => object,   spatialFocusArbitrator.buildSpatialRenderPlan
 *   fitRenderPlan: (viewer: object, plan: object) => object,   forgeSpatialFocusAdapter.fitSpatialRenderPlan
 *   listLoadedModels: (viewer: object) => object[],   forgeSpatialFocusAdapter.listLoadedModels
 *   themeElements: (dbIds: number[], colour: object) => void,   forgeUtils.setPermeativeElementsTheme
 *   toThemingColor: (hex: string) => object,   '#rrggbb' to the viewer's theming colour
 *   boxOf: (dbIds: number[], model?: object) => object,   forgeUtils.getBBBox
 *   takeScreenshot: (viewer: object, done: (dataUrl: string) => void) => void,   forgeService.takeScreenshot
 *   notify: { warning: (text: string) => void },
 *   events: { OBJECT_TREE_CREATED: string },
 *   unlockNavigation: () => void,   forgeviewer.TurnViewerLockOff (the Unlink button's navigation unlock)
 *   documents: object,   the document view's legacy functions (documents.js)
 *   plan: {   the 2D plan viewer's legacy functions (miniMap.js)
 *     launch: (urn: string, streamingApi: string, parentNode: object) => Promise,   forgeviewer.launchPlanViewer
 *     streamingApiFor: (region: string) => string,   regionUtils.getStreamingApi
 *     paneHost: () => object|null,   the 2D pane's #planViewer
 *     fitBox: (viewer: object, box: { min: {x,y}, max: {x,y} }) => void,   forgeUtils.zoomToFitBox2D
 *     reportError: (err: Error) => void,
 *   },
 * }} deps
 */
export function createViewer(deps) {
  assertDeps(deps);
  const { store } = deps;
  const ctx = Object.freeze({
    store,
    deps,
    current: () => store.state.viewer || null,
    service: () => store.state.forgeService,
    warnNoViewer: () => deps.notify.warning(VIEWER_MESSAGES.OPEN_3D_FIRST),
  });
  return Object.freeze({
    /** The raw Forge viewer, or null. A temporary escape hatch for code not yet behind an operation. */
    current: ctx.current,
    /** @returns {{ hasViewer: boolean, hasModel: boolean, hasInstanceTree: boolean, hasMainExtension: boolean, isOnScreen: boolean }} */
    readiness: () => readinessOf(ctx.current()),
    /** The model, its tree and the main extension are loaded and the canvas is on screen. */
    isReady: () => isReadyState(readinessOf(ctx.current())),
    ...createPaneOps(deps.panes),
    ...createSceneOps(ctx),
    ...createCameraOps(ctx),
    ...createCropOps(ctx),
    ...createViewLinkOps(ctx),
    ...createDocumentOps(ctx),
    ...createLinkedViewOps(ctx),
    ...createMiniMapOps(ctx),
  });
}
